import { SimplifiedAppointment } from '@/types/appointment';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type { SectionType } from './slices/sectionRecordingSlice';

// Auth selectors
const selectAuth = (state: RootState) => state.auth;
const selectUser = (state: RootState) => state.auth.user;
const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
const selectAuthLoading = (state: RootState) => state.auth.loading;
const selectIntegrationToken = (state: RootState) =>
  state.auth.integrationToken;

// Select partnerId from integration token or user's ev_integration_partner_id as fallback
const selectPartnerId = (state: RootState) => {
  // First try to get from integration token
  const integrationToken = state.auth.integrationToken;
  if (integrationToken?.partnerId) {
    return integrationToken.partnerId;
  }

  // Fallback to user's ev_integration_partner_id
  const user = state.auth.user;
  if (user?.ev_integration_partner_id) {
    return user.ev_integration_partner_id;
  }

  return null;
};

export const AuthStateSelector = {
  root: selectAuth,
  user: selectUser,
  isAuthenticated: selectIsAuthenticated,
  loading: selectAuthLoading,
  integrationToken: selectIntegrationToken,
  partnerId: selectPartnerId,
};

// Integration selectors
const selectIntegration = (state: RootState) => state.integration;
const selectIntegrationPartner = (state: RootState) =>
  state.integration.partner;
const selectIntegrationEndpoints = (state: RootState) =>
  state.integration.endpoints;
const selectIntegrationDataFetched = (state: RootState) =>
  state.integration.dataFetched;
const selectIntegrationLoading = (state: RootState) =>
  state.integration.loading;
const selectIntegrationError = (state: RootState) => state.integration.error;

export const IntegrationStateSelector = {
  root: selectIntegration,
  partner: selectIntegrationPartner,
  endpoints: selectIntegrationEndpoints,
  dataFetched: selectIntegrationDataFetched,
  loading: selectIntegrationLoading,
  error: selectIntegrationError,
};

// Data selectors
const selectData = (state: RootState) => state.data;
const selectAppointments = (state: RootState) => state.data.appointments;
const selectAppointmentsData = (state: RootState) =>
  state.data.appointments.data;
const selectSelectedAppointment = (state: RootState) =>
  state.data.appointments.selectedAppointment;
const selectAppointmentsLoading = (state: RootState) =>
  state.data.appointments.loading;
const selectAppointmentsError = (state: RootState) =>
  state.data.appointments.error;
const selectCache = (state: RootState) => state.data.cache;

export const AppointmentsStateSelector = {
  root: selectAppointments,
  appointmentsData: selectAppointmentsData,
  selectedAppointment: selectSelectedAppointment,
  loading: selectAppointmentsLoading,
  error: selectAppointmentsError,
};

// User Locations selectors
const selectUserLocations = (state: RootState) => state.data.userLocations.data;
const selectUserLocationsLoading = (state: RootState) =>
  state.data.userLocations.loading;
const selectUserLocationsError = (state: RootState) =>
  state.data.userLocations.error;
const selectUserLocationsFetched = (state: RootState) =>
  state.data.userLocations.fetched;

export const UserLocationsStateSelector = {
  locations: selectUserLocations,
  loading: selectUserLocationsLoading,
  error: selectUserLocationsError,
  fetched: selectUserLocationsFetched,
};

// Recording selectors
const selectRecording = (state: RootState) => state.recording;
const selectIsRecording = (state: RootState) => state.recording.isRecording;
const selectIsPaused = (state: RootState) => state.recording.isPaused;

const selectIsPausedFromDatabase = (state: RootState) =>
  (state.data.appointments.selectedAppointment as SimplifiedAppointment)
    ?.status === 'PAUSED';
const selectIsStopped = (state: RootState) => state.recording.isStopped;
const selectRecordingConfig = (state: RootState) => state.recording.config;
const selectRecordingError = (state: RootState) => state.recording.error;
const selectIsJobCompleted = (state: RootState) =>
  state.recording.isJobCompleted;
const selectPollingActive = (state: RootState) => state.recording.pollingActive;
const selectProcessingStarted = (state: RootState) =>
  state.recording.processingStarted;

export const RecordingStateSelector = {
  root: selectRecording,
  isRecording: selectIsRecording,
  isPaused: selectIsPaused,
  isPausedFromDatabase: selectIsPausedFromDatabase,
  isStopped: selectIsStopped,
  config: selectRecordingConfig,
  error: selectRecordingError,
  isJobCompleted: selectIsJobCompleted,
  pollingActive: selectPollingActive,
  processingStarted: selectProcessingStarted,
};

// Computed selectors
export const selectUserDisplayName = createSelector([selectUser], (user) => {
  if (!user) return 'User';
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || 'User';
});

export const selectUserInitials = createSelector([selectUser], (user) => {
  if (!user) return 'U';
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (!fullName) return 'U';
  return fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
});

export const selectCachedData = (key: string) =>
  createSelector([selectCache], (cache) => {
    const cached = cache[key];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      return null; // Expired
    }

    return cached.data;
  });

// Integration computed selectors
export const selectIntegrationEndpointsByAction = createSelector(
  [selectIntegrationEndpoints],
  (endpoints) => {
    const endpointsByAction = new Map<string, (typeof endpoints)[0]>();
    endpoints.forEach((endpoint) => {
      endpointsByAction.set(endpoint.action, endpoint);
    });
    return endpointsByAction;
  }
);

export const selectIntegrationEndpointsByEnvironment = createSelector(
  [selectIntegrationEndpoints],
  (endpoints) => {
    const endpointsByEnv = {
      development: endpoints.filter((ep) => ep.environment === 'development'),
      staging: endpoints.filter((ep) => ep.environment === 'staging'),
      production: endpoints.filter((ep) => ep.environment === 'production'),
    };
    return endpointsByEnv;
  }
);

export const selectIntegrationStatus = createSelector(
  [
    selectIntegrationPartner,
    selectIntegrationEndpoints,
    selectIntegrationDataFetched,
    selectIntegrationLoading,
    selectIntegrationError,
  ],
  (partner, endpoints, dataFetched, loading, error) => ({
    hasPartner: !!partner,
    hasEndpoints: endpoints.length > 0,
    dataFetched,
    loading,
    error,
    isReady:
      !!partner && endpoints.length > 0 && dataFetched && !loading && !error,
  })
);

export const selectUserLocationsStatus = createSelector(
  [
    selectUserLocations,
    selectUserLocationsLoading,
    selectUserLocationsError,
    selectUserLocationsFetched,
  ],
  (locations, loading, error, fetched) => ({
    hasLocations: !!locations && locations.length > 0,
    locationsCount: locations?.length || 0,
    loading,
    error,
    fetched,
    isReady: fetched && !loading && !error,
  })
);

// All locations are considered active
export const selectActiveUserLocations = (state: RootState) => {
  const locations = selectUserLocations(state);
  return locations || [];
};

export const selectUserLocationById = createSelector(
  [selectUserLocations, (_state: RootState, locationId: string) => locationId],
  (locations, locationId) => {
    if (!locations) return null;
    return (
      locations.find((location) => location.locationId === locationId) || null
    );
  }
);

// Selected Location selectors
const selectSelectedLocation = (state: RootState) =>
  state.data.selectedLocation;

const selectSelectedLocationId = (state: RootState) =>
  state.data.selectedLocation.locationId;

const selectSelectedLocationName = (state: RootState) =>
  state.data.selectedLocation.locationName;

const selectHasSelectedLocation = (state: RootState) =>
  !!state.data.selectedLocation.locationId;

export const SelectedLocationStateSelector = {
  root: selectSelectedLocation,
  locationId: selectSelectedLocationId,
  locationName: selectSelectedLocationName,
  hasLocation: selectHasSelectedLocation,
};

// Section Recording selectors
const selectSectionRecording = (state: RootState) => state.sectionRecording;
const selectActiveRecording = (state: RootState) =>
  state.sectionRecording.activeRecording;
const selectRecordedSections = (state: RootState) =>
  state.sectionRecording.recordedSections;
const selectSectionRecordingContext = (state: RootState) => ({
  appointmentId: state.sectionRecording.appointmentId,
  patientId: state.sectionRecording.patientId,
  locationId: state.sectionRecording.locationId,
  scribeJobId: state.sectionRecording.scribeJobId,
});

export const SectionRecordingStateSelector = {
  root: selectSectionRecording,
  activeRecording: selectActiveRecording,
  recordedSections: selectRecordedSections,
  context: selectSectionRecordingContext,
};

// Section Recording computed selectors
export const selectIsAnySectionRecording = createSelector(
  [selectActiveRecording],
  (activeRecording) =>
    activeRecording !== null &&
    (activeRecording.status === 'recording' ||
      activeRecording.status === 'paused')
);

export const selectActiveSectionName = createSelector(
  [selectActiveRecording],
  (activeRecording) => activeRecording?.sectionName || null
);

export const selectActiveRecordingStatus = createSelector(
  [selectActiveRecording],
  (activeRecording) => activeRecording?.status || 'idle'
);

export const selectPendingUploads = createSelector(
  [selectRecordedSections],
  (recordedSections) =>
    recordedSections.filter((section) => section.uploadStatus === 'pending')
);

export const selectFailedUploads = createSelector(
  [selectRecordedSections],
  (recordedSections) =>
    recordedSections.filter((section) => section.uploadStatus === 'failed')
);

export const selectUploadingRecordings = createSelector(
  [selectRecordedSections],
  (recordedSections) =>
    recordedSections.filter((section) => section.uploadStatus === 'uploading')
);

export const selectHasSectionRecordingContext = createSelector(
  [selectSectionRecordingContext],
  (context) =>
    !!(
      context.appointmentId &&
      context.patientId &&
      context.locationId &&
      context.scribeJobId
    )
);

export const selectRecordedSectionById = createSelector(
  [selectRecordedSections, (_state: RootState, id: string) => id],
  (recordedSections, id) =>
    recordedSections.find((section) => section.id === id) || null
);

export const selectRecordingSectionsDuration = createSelector(
  [selectRecordedSections],
  (recordedSections) =>
    recordedSections.reduce((total, section) => total + section.duration, 0)
);

export const selectCompletedSections = createSelector(
  [selectRecordedSections],
  (recordedSections) =>
    recordedSections.filter((section) => section.uploadStatus === 'completed')
);

export const selectCompletedSectionsByName = createSelector(
  [
    selectRecordedSections,
    (_state: RootState, sectionName: SectionType) => sectionName,
  ],
  (recordedSections, sectionName) =>
    recordedSections.filter(
      (section) =>
        section.sectionName === sectionName &&
        section.uploadStatus === 'completed'
    )
);
